import type { CancelBeneficiarySuggestion } from "@/dashboard/inventory_management/cancel/cancelBeneficiariesApi";
import type { CancellationTargetLookup } from "@/dashboard/inventory_management/common/cancellationTargets";
import {
  isAnyAibRow,
  templateUsesAddressPlaceholder,
} from "@/dashboard/inventory_management/common/cancelStatementHelpers";
import type { BeneficiaryTypeEnum } from "@/types";
import { computed, toValue, type MaybeRefOrGetter } from "vue";

/** Minimal editor-row shape the advisories read (the editor's CompactCancelRow is a superset). */
interface AdvisoryRow {
  beneficiary_name?: string;
  registry_country_region?: string | null;
  address?: string | null;
}

/** A row's beneficiary is remembered with a different type than the shared one. */
export interface BeneficiaryTypeConflict {
  name: string;
  rememberedType: BeneficiaryTypeEnum;
}

export interface CancelEditorAdvisoriesSources {
  /** Live editor rows (unmaterialized — the Any AIB sentinel may still be present). */
  rows: MaybeRefOrGetter<AdvisoryRow[]>;
  /** Resolves the Any AIB sentinel to its effective region for the mismatch advisory. */
  targets: MaybeRefOrGetter<CancellationTargetLookup>;
  /** The shared cancellation-purpose template, checked for the {address} placeholder. */
  purposeTemplate: MaybeRefOrGetter<string | null | undefined>;
  /** Registry regions of the selected inventory, used for the mismatch advisory. */
  selectedRegions: MaybeRefOrGetter<string[]>;
  /** Remembered beneficiaries offered by the name combobox, for the type-conflict warning. */
  beneficiarySuggestions: MaybeRefOrGetter<CancelBeneficiarySuggestion[]>;
  /** The shared beneficiary type every statement is submitted with. */
  sharedBeneficiaryType: MaybeRefOrGetter<BeneficiaryTypeEnum>;
}

/**
 * Derived, non-blocking advisories surfaced around the cancel statements editor (DEV-576 grouped
 * these together): the region-mismatch banner, the Any AIB caveat and {address}-placeholder hint,
 * the missing-address warning, and the remembered beneficiary-type conflict. The cancellation rule
 * warnings come from the backend instead (useCancelRuleEvaluation). Pure derivations over the
 * editor's reactive state — kept out of the SFC so the warning logic is cohesive and testable.
 */
export function useCancelEditorAdvisories(
  sources: CancelEditorAdvisoriesSources
) {
  const hasRegionMismatch = computed(() => {
    const selected = toValue(sources.selectedRegions);
    if (selected.length === 0) {
      return false;
    }
    if (selected.length > 1) {
      return true;
    }
    const selectedRegion = selected[0];
    return toValue(sources.rows).some((row) => {
      const registry = row.registry_country_region;
      if (!registry) return false;
      return (
        toValue(sources.targets).effectiveRegion(registry) !== selectedRegion
      );
    });
  });

  const anyRowIsAnyAib = computed(() =>
    toValue(sources.rows).some((row) =>
      isAnyAibRow(row.registry_country_region)
    )
  );

  // Non-blocking hint that the {address} placeholder is available: shown when an Any AIB row is
  // present but the shared purpose template does not yet reference {address}. DEV-576 surfaces this
  // as its own advisory pane (grouped with the region-mismatch banner) instead of under the purpose
  // input. Once {address} is added the hint has served its purpose and steps aside.
  const shouldSuggestAddressPlaceholder = computed(
    () =>
      anyRowIsAnyAib.value &&
      !templateUsesAddressPlaceholder(toValue(sources.purposeTemplate))
  );

  // The purpose template uses {address} but some row has no address. Surfaced as an error but does
  // NOT block submission.
  const hasAddressPlaceholderWithoutAddress = computed(() => {
    if (!templateUsesAddressPlaceholder(toValue(sources.purposeTemplate))) {
      return false;
    }
    return toValue(sources.rows).some((row) => !row.address?.trim());
  });

  /**
   * Rows whose remembered beneficiary type disagrees with `candidateType`, deduplicated so two rows
   * naming the same beneficiary count once. Shared with the editor's adopt-on-pick guard so the two
   * cannot disagree about what a conflict is. A null remembered type is silence, not disagreement.
   */
  const beneficiaryTypeConflictsWith = (
    candidateType: BeneficiaryTypeEnum
  ): BeneficiaryTypeConflict[] => {
    const suggestions = toValue(sources.beneficiarySuggestions);
    const conflicts = new Map<string, BeneficiaryTypeConflict>();
    for (const row of toValue(sources.rows)) {
      const remembered = findSuggestionByName(
        suggestions,
        row.beneficiary_name
      );
      if (
        !remembered?.beneficiary_type ||
        remembered.beneficiary_type === candidateType
      ) {
        continue;
      }
      conflicts.set(remembered.beneficiary_name, {
        name: remembered.beneficiary_name,
        rememberedType: remembered.beneficiary_type,
      });
    }
    return [...conflicts.values()];
  };

  // Surfaced as an error but does NOT block submission: the memory may simply be stale, and a
  // mixed-type form is inexpressible anyway — beneficiary type is a single shared field.
  const beneficiaryTypeConflicts = computed(() =>
    beneficiaryTypeConflictsWith(toValue(sources.sharedBeneficiaryType))
  );

  return {
    hasRegionMismatch,
    anyRowIsAnyAib,
    shouldSuggestAddressPlaceholder,
    hasAddressPlaceholderWithoutAddress,
    beneficiaryTypeConflicts,
    beneficiaryTypeConflictsWith,
  };
}

/**
 * The remembered beneficiary for a row's name, matched the way the backend deduplicates history —
 * case- and whitespace-insensitively — so a hand-typed name resolves to the same entry as a picked
 * suggestion.
 */
const findSuggestionByName = (
  suggestions: CancelBeneficiarySuggestion[],
  beneficiaryName: string | null | undefined
): CancelBeneficiarySuggestion | undefined => {
  const normalized = normalizeName(beneficiaryName);
  return normalized
    ? suggestions.find(
        (suggestion) =>
          normalizeName(suggestion.beneficiary_name) === normalized
      )
    : undefined;
};

const normalizeName = (name: string | null | undefined): string =>
  name?.trim().toLowerCase() ?? "";
